import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Mail, Lock, Eye, EyeOff, ArrowRight, ArrowLeft, Building2, Stethoscope,
  Check, User, AlertCircle, Sparkles, Shield, CheckCircle
} from 'lucide-react'
import { useAuth } from '../../App'
import { AnimatedLogo } from '../../components/healthfluence'

const STEPS = {
  ACCOUNT_TYPE: 0,
  BASIC_INFO: 1,
  VERIFY_EMAIL: 2,
}

function RegisterPage() {
  const [searchParams] = useSearchParams()
  const initialType = searchParams.get('type') || ''

  const [currentStep, setCurrentStep] = useState(initialType ? STEPS.BASIC_INFO : STEPS.ACCOUNT_TYPE)
  const [userType, setUserType] = useState(initialType)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false,
  })
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [registrationData, setRegistrationData] = useState(null)
  const [verificationCode, setVerificationCode] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)

  const navigate = useNavigate()
  const { setRegisteredUser } = useAuth()

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    setError('')
  }

  const selectAccountType = (type) => {
    setUserType(type)
    setCurrentStep(STEPS.BASIC_INFO)
  }

  const validateStep = () => {
    if (currentStep === STEPS.BASIC_INFO) {
      if (!formData.firstName.trim() || !formData.lastName.trim()) {
        setError('Please enter your first and last name')
        return false
      }
      if (!formData.email.trim() || !formData.email.includes('@')) {
        setError('Please enter a valid email address')
        return false
      }
      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters')
        return false
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match')
        return false
      }
      if (!formData.agreeTerms) {
        setError('Please agree to the terms and conditions')
        return false
      }
    }
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateStep()) return

    setIsLoading(true)
    setError('')

    try {
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          userType,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed')
      }

      setRegistrationData(data.user)
      setCurrentStep(STEPS.VERIFY_EMAIL)
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyEmail = async () => {
    if (!registrationData) return

    setIsVerifying(true)
    setError('')

    try {
      const response = await fetch('/api/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: registrationData.id,
          token: registrationData.verificationToken,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Verification failed')
      }

      // Store user data and navigate to onboarding
      setRegisteredUser(data.user)
      navigate(`/onboarding/${userType}`)
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.')
    } finally {
      setIsVerifying(false)
    }
  }

  // For demo purposes, auto-verify after a short delay to simulate email verification
  useEffect(() => {
    if (currentStep === STEPS.VERIFY_EMAIL && registrationData) {
      const timer = setTimeout(() => {
        // Simulate receiving the verification email
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [currentStep, registrationData])

  const renderAccountTypeStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Welcome to Healthfluence</h2>
        <p className="mt-2 text-gray-600">How would you like to join our trusted healthcare community?</p>
      </div>

      <div className="grid gap-4">
        <button
          type="button"
          onClick={() => selectAccountType('brand')}
          className="flex items-start p-6 rounded-xl border-2 border-gray-200 hover:border-hf-teal-500 hover:bg-hf-teal-50 transition-all text-left group"
        >
          <div className="w-14 h-14 bg-hf-teal-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-hf-teal-200 transition-colors">
            <Building2 className="w-7 h-7 text-hf-teal-600" />
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-hf-teal-700">I'm a Healthcare Brand</h3>
            <p className="mt-1 text-gray-600 text-sm">Partner with trusted creators to reach patients and healthcare professionals</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="text-xs bg-hf-teal-100 text-hf-teal-700 px-2 py-1 rounded-full">MedTech</span>
              <span className="text-xs bg-hf-teal-100 text-hf-teal-700 px-2 py-1 rounded-full">Pharma</span>
              <span className="text-xs bg-hf-teal-100 text-hf-teal-700 px-2 py-1 rounded-full">Digital Health</span>
              <span className="text-xs bg-hf-teal-100 text-hf-teal-700 px-2 py-1 rounded-full">Wellness</span>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-hf-teal-600 ml-auto mt-2 transition-colors" />
        </button>

        <button
          type="button"
          onClick={() => selectAccountType('creator')}
          className="flex items-start p-6 rounded-xl border-2 border-gray-200 hover:border-teal-500 hover:bg-teal-50 transition-all text-left group"
        >
          <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-teal-200 transition-colors">
            <Stethoscope className="w-7 h-7 text-teal-600" />
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-teal-700">I'm a Healthcare Creator</h3>
            <p className="mt-1 text-gray-600 text-sm">Connect with premium brands and monetize your healthcare expertise</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full">Physicians</span>
              <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full">Nurses</span>
              <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full">Patient Advocates</span>
              <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full">Educators</span>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-teal-600 ml-auto mt-2 transition-colors" />
        </button>
      </div>

      <p className="text-center text-gray-600 mt-6">
        Already have an account?{' '}
        <Link to="/login" className="text-hf-teal-600 hover:text-hf-teal-700 font-medium">
          Sign in
        </Link>
      </p>
    </div>
  )

  const renderBasicInfoStep = () => (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="mb-6">
        <button
          type="button"
          onClick={() => {
            setCurrentStep(STEPS.ACCOUNT_TYPE)
            setUserType('')
          }}
          className="flex items-center text-gray-600 hover:text-gray-900 text-sm"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Change account type
        </button>
      </div>

      <div className="flex items-center justify-center mb-6">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          userType === 'brand' ? 'bg-hf-teal-100' : 'bg-teal-100'
        }`}>
          {userType === 'brand' ? (
            <Building2 className={`w-6 h-6 text-hf-teal-600`} />
          ) : (
            <Stethoscope className={`w-6 h-6 text-teal-600`} />
          )}
        </div>
        <div className="ml-3">
          <h2 className="text-xl font-bold text-gray-900">
            Create your {userType === 'brand' ? 'Brand' : 'Creator'} account
          </h2>
          <p className="text-gray-600 text-sm">Just a few details to get started</p>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="firstName" className="label">First name</label>
          <input
            id="firstName"
            name="firstName"
            type="text"
            value={formData.firstName}
            onChange={handleChange}
            className="input"
            placeholder="Sarah"
            required
          />
        </div>
        <div>
          <label htmlFor="lastName" className="label">Last name</label>
          <input
            id="lastName"
            name="lastName"
            type="text"
            value={formData.lastName}
            onChange={handleChange}
            className="input"
            placeholder="Johnson"
            required
          />
        </div>
      </div>

      <div>
        <label htmlFor="email" className="label">Work email</label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            className="input pl-10"
            placeholder="you@company.com"
            required
          />
        </div>
      </div>

      <div>
        <label htmlFor="password" className="label">Password</label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            className="input pl-10 pr-10"
            placeholder="Min 8 characters"
            minLength={8}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div>
        <label htmlFor="confirmPassword" className="label">Confirm password</label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            id="confirmPassword"
            name="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleChange}
            className="input pl-10"
            placeholder="Confirm your password"
            required
          />
        </div>
      </div>

      <div className="flex items-start">
        <input
          id="agreeTerms"
          name="agreeTerms"
          type="checkbox"
          checked={formData.agreeTerms}
          onChange={handleChange}
          className="mt-1 h-4 w-4 text-hf-teal-600 border-gray-300 rounded focus:ring-hf-teal-500"
          required
        />
        <label htmlFor="agreeTerms" className="ml-2 text-sm text-gray-600">
          I agree to the{' '}
          <a href="#" className="text-hf-teal-600 hover:text-hf-teal-700">Terms of Service</a>
          {' '}and{' '}
          <a href="#" className="text-hf-teal-600 hover:text-hf-teal-700">Privacy Policy</a>
        </label>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className={`w-full py-3 rounded-lg font-medium flex items-center justify-center transition-colors ${
          userType === 'brand'
            ? 'bg-hf-teal-600 hover:bg-hf-teal-700 text-white'
            : 'bg-teal-600 hover:bg-teal-700 text-white'
        }`}
      >
        {isLoading ? 'Creating account...' : 'Create account'}
        {!isLoading && <ArrowRight className="ml-2 w-4 h-4" />}
      </button>

      <p className="text-center text-gray-600 text-sm">
        Already have an account?{' '}
        <Link to="/login" className="text-hf-teal-600 hover:text-hf-teal-700 font-medium">
          Sign in
        </Link>
      </p>
    </form>
  )

  const renderVerifyEmailStep = () => (
    <div className="text-center space-y-6">
      <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center ${
        userType === 'brand' ? 'bg-hf-teal-100' : 'bg-teal-100'
      }`}>
        <Mail className={`w-10 h-10 ${userType === 'brand' ? 'text-hf-teal-600' : 'text-teal-600'}`} />
      </div>

      <div>
        <h2 className="text-2xl font-bold text-gray-900">Check your email</h2>
        <p className="mt-2 text-gray-600">
          We sent a verification link to<br />
          <span className="font-medium text-gray-900">{formData.email}</span>
        </p>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center justify-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      <div className="bg-gray-50 rounded-xl p-6">
        <p className="text-sm text-gray-600 mb-4">
          <Sparkles className="w-4 h-4 inline mr-1 text-yellow-500" />
          <strong>For the pilot:</strong> Click the button below to simulate email verification
        </p>
        <button
          onClick={handleVerifyEmail}
          disabled={isVerifying}
          className={`w-full py-3 rounded-lg font-medium flex items-center justify-center transition-colors ${
            userType === 'brand'
              ? 'bg-hf-teal-600 hover:bg-hf-teal-700 text-white'
              : 'bg-teal-600 hover:bg-teal-700 text-white'
          }`}
        >
          {isVerifying ? (
            'Verifying...'
          ) : (
            <>
              <CheckCircle className="w-5 h-5 mr-2" />
              Verify Email & Continue
            </>
          )}
        </button>
      </div>

      <p className="text-sm text-gray-500">
        Didn't receive the email?{' '}
        <button className="text-hf-teal-600 hover:text-hf-teal-700 font-medium">
          Resend
        </button>
      </p>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-md">
          <Link to="/" className="flex items-center space-x-2 mb-8">
            <AnimatedLogo size="xs" />
            <span className="text-xl font-bold bg-gradient-to-r from-hf-teal-600 to-hf-navy-700 bg-clip-text text-transparent">Healthfluence</span>
          </Link>

          {/* Progress Indicator */}
          {currentStep !== STEPS.ACCOUNT_TYPE && (
            <div className="mb-8 flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                currentStep >= STEPS.BASIC_INFO
                  ? userType === 'brand' ? 'bg-hf-teal-600 text-white' : 'bg-teal-600 text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}>
                {currentStep > STEPS.BASIC_INFO ? <Check className="w-4 h-4" /> : '1'}
              </div>
              <div className={`flex-1 h-1 mx-2 ${
                currentStep >= STEPS.VERIFY_EMAIL
                  ? userType === 'brand' ? 'bg-hf-teal-600' : 'bg-teal-600'
                  : 'bg-gray-200'
              }`} />
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                currentStep >= STEPS.VERIFY_EMAIL
                  ? userType === 'brand' ? 'bg-hf-teal-600 text-white' : 'bg-teal-600 text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}>
                2
              </div>
            </div>
          )}

          {currentStep === STEPS.ACCOUNT_TYPE && renderAccountTypeStep()}
          {currentStep === STEPS.BASIC_INFO && renderBasicInfoStep()}
          {currentStep === STEPS.VERIFY_EMAIL && renderVerifyEmailStep()}
        </div>
      </div>

      {/* Right Side - Branding */}
      <div className={`hidden lg:flex lg:flex-1 items-center justify-center p-12 ${
        userType === 'creator'
          ? 'bg-gradient-to-br from-teal-600 to-teal-700'
          : 'bg-gradient-to-br from-hf-teal-600 to-hf-teal-700'
      }`}>
        <div className="max-w-md">
          <div className="flex justify-start mb-8">
            <AnimatedLogo size="md" variant="light" />
          </div>

          <h2 className="text-3xl font-bold text-white mb-6">
            {userType === 'creator'
              ? 'Build a sustainable career in healthcare influence'
              : 'The trusted circle for healthcare marketing'}
          </h2>

          <ul className="space-y-4">
            {(userType === 'creator' ? [
              'Fair, transparent compensation',
              'Work with premium healthcare brands',
              'Built-in compliance support',
              'Professional growth tools',
            ] : [
              'Access vetted healthcare creators',
              'Compliance-first workflows',
              'Launch campaigns in weeks',
              'Real-time analytics and ROI',
            ]).map((item, index) => (
              <li key={index} className="flex items-center text-white">
                <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center mr-3 flex-shrink-0">
                  <Check className="w-4 h-4" />
                </div>
                {item}
              </li>
            ))}
          </ul>

          <div className="mt-12 p-6 bg-white/10 rounded-xl">
            <p className="text-white/90 italic">
              "Healthfluence has transformed how I work with healthcare brands. The process is seamless, and I know every partnership meets compliance standards."
            </p>
            <div className="mt-4 flex items-center">
              <img
                src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=50&h=50&fit=crop"
                alt="Dr. Emily Watson"
                className="w-10 h-10 rounded-full"
              />
              <div className="ml-3">
                <p className="text-white font-medium">Dr. Emily Watson</p>
                <p className="text-white/70 text-sm">Health Educator • Verified</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RegisterPage
